import React from "react";
import { Stethoscope, Scissors, Activity, ScanLine, FlaskConical, CalendarPlus, Apple, MessageCircle } from "lucide-react";

const services = [
  {
    icon: Stethoscope,
    title: "Medicina General",
    description: "Revisiones, vacunas y seguimiento integral de la salud de tu mascota.",
    testid: "service-general",
  },
  {
    icon: Scissors,
    title: "Cirugía",
    description: "Intervenciones quirúrgicas con protocolos seguros y anestesia controlada.",
    testid: "service-surgery",
  },
  {
    icon: Activity,
    title: "Ecografía",
    description: "Diagnóstico por ultrasonido no invasivo, rápido y de alta precisión.",
    testid: "service-ultrasound",
  },
  {
    icon: ScanLine,
    title: "Radiografía",
    description: "Imagen digital de calidad para detectar fracturas y patologías internas.",
    testid: "service-xray",
  },
  {
    icon: FlaskConical,
    title: "Laboratorio",
    description: "Analíticas clínicas con resultados ágiles para diagnósticos certeros.",
    testid: "service-lab",
  },
  {
    icon: CalendarPlus,
    title: "Planes de Salud",
    description: "Planes de salud personalizados para el cuidado preventivo de tu mascota durante todo el año.",
    testid: "service-lab",
  },
  {
    icon: Apple,
    title: "Alimentación",
    description: "Revisión de la dieta y recomendaciones nutricionales para el bienestar de tu mascota.",
    testid: "service-lab",
  },
];

export const Services = () => {
  return (
    <section
      id="servicios"
      data-testid="services-section"
      className="py-20 md:py-28 bg-gradient-to-b from-violet-50/40 via-white to-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <span className="inline-block text-sm font-semibold text-violet-600 uppercase tracking-wider mb-4">
            Nuestros servicios
          </span>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-800 leading-tight">
            Todo lo que tu mascota necesita,{" "}
            <span className="text-violet-600">en un mismo sitio.</span>
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Atención clínica completa para perros y gatos, con equipamiento
            moderno y un trato cercano en cada visita.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((s, idx) => {
            const Icon = s.icon;
            const isFeatured = idx === 0;
            return (
              <article
                key={s.testid}
                data-testid={s.testid}
                className={`group relative rounded-3xl p-8 border transition-all duration-300 hover:-translate-y-1 ${
                  isFeatured
                    ? "bg-gradient-to-br from-violet-600 to-violet-700 text-white border-violet-600 shadow-[0_20px_50px_rgba(124,58,237,0.25)]"
                    : "bg-white border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_50px_rgb(0,0,0,0.08)]"
                }`}
              >
                <div
                  className={`w-14 h-14 rounded-2xl flex items-center justify-center ring-1 ${
                    isFeatured
                      ? "bg-white/15 text-white ring-white/20"
                      : "bg-violet-50 text-violet-600 ring-violet-100"
                  }`}
                >
                  <Icon className="w-7 h-7" strokeWidth={2.2} />
                </div>
                <h3
                  className={`mt-6 font-['Outfit'] text-xl md:text-2xl font-semibold ${
                    isFeatured ? "text-white" : "text-slate-800"
                  }`}
                >
                  {s.title}
                </h3>
                <p
                  className={`mt-3 leading-relaxed ${
                    isFeatured ? "text-violet-50/90" : "text-slate-600"
                  }`}
                >
                  {s.description}
                </p>
              </article>
            );
          })}

          {/* Final CTA card to balance grid (5 services + 1 CTA = 6) */}
          <article
            data-testid="services-cta-card"
            className="group relative rounded-3xl p-8 border border-dashed border-violet-300 bg-violet-50/40 flex flex-col justify-center"
          >
            <p className="font-['Outfit'] text-xl font-semibold text-slate-800">
              ¿Tu mascota necesita un servicio concreto?
            </p>
            <p className="mt-2 text-slate-600">
              Llámanos y te asesoramos sin compromiso.
            </p>
            <div className="mt-5 flex items-center gap-4">
              <a
                href="tel:684366280"
                data-testid="services-call-link"
                className="inline-flex items-center gap-2 font-semibold text-violet-700 hover:text-violet-900 transition-colors"
              >
                <span aria-hidden="true" className="text-xl">📞</span>
                684 36 62 80
                <span aria-hidden="true" className="transition-transform group-hover:translate-x-1">→</span>
              </a>

              <a
                href="https://wa.me/34684366280"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="services-whatsapp-link"
                className="inline-flex items-center gap-2 font-semibold text-green-600 hover:text-green-800 transition-colors"
              >
                <span className="sr-only">Abrir chat en WhatsApp</span>
                <MessageCircle className="w-5 h-5" strokeWidth={2} />
              </a>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};
